import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  createStyles,
  Grid,
  Badge,
  makeStyles,
  Tabs as MuiTabs,
  Tab as MuiTab,
  Theme,
  useTheme,
  useMediaQuery,
} from '@material-ui/core';
import { useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import { useRouteMatch } from 'react-router';
import { clipLastSegmentRoute } from '../../utility/path';
import keyCodeConverter from '../../utility/keyCodeConverter';
import { Tab } from '../../services/TabLinkService/types';

const useStyles = makeStyles(({ breakpoints, palette, colors }: Theme) => createStyles({
  tabsContainer: {
    flexGrow: 1,
  },

  tabsRoot: {
    minHeight: '35px',

    [breakpoints.down('md')]: {
      '& div:first-child': {
        width: '0',
      },
      '& div.MuiButtonBase-root': {
        width: '30px',
      },
    },
  },
  tabsFlexContainer: {
    [breakpoints.up('sm')]: {
      // justifyContent: 'center',
    },
  },
  tabsScrollButtons: {
    width: 30,
  },
  tabsIndicator: {
    display: 'none',
  },

  tabRoot: {
    minWidth: 55,
    minHeight: '35px',
    textTransform: 'capitalize',
    color: colors.tabs,
    opacity: 1,
    '&:focus': {
      outline: 'none',
    },
    '&:hover': {
      color: palette.tabs.hover,
    },
  },
  tabSelected: {
    borderRadius: '5px',
    backgroundColor: palette.tabs.selected,
  },
  tabWrapper: {
    alignItems: 'flex-start',
    fontFamily: 'Montserrat, sans-serif',
    fontWeight: 500,
    fontSize: 14,
  },
  badge: {
    top: '0px',
    right: '-5px',
    height: '11px',
    minWidth: '9px',
    fontSize: '9px',
  },

  tabContentRoot: {
    width: '100%',
    marginTop: '1em',
  },
}), { name: 'Tabs' });


const pager = (min: number, max: number) => ({
  next: (state: number) => (state + 1 > max ? min : state + 1),
  prev: (state: number) => (state - 1 < min ? max : state - 1),
});

type Props<P> = {
  tabs: Tab[];
};

const findIndexTab = (tabs: Tab[], url: string): number => {
  const index = tabs.findIndex((item) => {
    return item.to === url || (item.additionalLinks && item.additionalLinks.includes(url));
  });

  if (index === -1 && url !== '') {
    return findIndexTab(tabs, clipLastSegmentRoute(url));
  }

  return index === -1 ? 0 : index;
};

const TabsLink = <P extends object>({ tabs }: Props<P>): React.ReactElement | null => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { url } = useRouteMatch();

  const [tab, setTab] = useState(findIndexTab(tabs, url));

  useEffect(() => {
    if (tabs[tab].to !== url && tabs[tab].to !== '') {
      const index = findIndexTab(tabs, url);
      setTab(index);
    }
  }, [tabs, tab, setTab, url]);

  const handleTabChange = useCallback((event, newValue) => {
    setTab(newValue);
    dispatch(push(tabs[newValue].to));
  }, [setTab, dispatch, tabs]);

  const { prev, next } = useMemo(() => pager(0, tabs.length - 1), [tabs.length]);

  useEffect(() => {
    const keyHandler = (event: KeyboardEvent) => {
      if (event.keyCode === keyCodeConverter('bracketLeft') && event.ctrlKey) {
        event.preventDefault();
        const index = prev(tab);
        handleTabChange(null, index);
      }
      if (event.keyCode === keyCodeConverter('bracketRight') && event.ctrlKey) {
        event.preventDefault();
        const index = next(tab);
        handleTabChange(null, index);
      }
    };

    window.addEventListener('keydown', keyHandler);

    return () => window.removeEventListener('keydown', keyHandler);
  }, [tab, prev, next, handleTabChange]);

  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Grid className={classes.tabsContainer}>
      <MuiTabs
        value={tab}
        onChange={handleTabChange}
        indicatorColor="primary"
        orientation={compact ? 'horizontal' : 'vertical'}
        variant="scrollable"
        scrollButtons={compact ? 'on' : 'auto'}
        classes={{
          flexContainer: classes.tabsFlexContainer,
          scrollButtons: classes.tabsScrollButtons,
          root: classes.tabsRoot,
          indicator: classes.tabsIndicator,
        }}
      >
        {tabs.map(
          ({ label, to, disabled }, index) => (
            <MuiTab
              tabIndex={-1}
              disabled={disabled}
              label={(
                <Badge
                  color="default"
                  classes={{
                    badge: classes.badge,
                  }}
                >
                  {label}
                </Badge>
              )}
              classes={{
                root: classes.tabRoot,
                selected: classes.tabSelected,
                wrapper: classes.tabWrapper,
              }}
              key={to}
              data-intercom-target={`${label} Tab`}
            />
          ),
        )}
      </MuiTabs>
    </Grid>
  );
};

export default TabsLink;
